/* Order of Battle and contact list.
 *
 * ── This is the accessibility path for the whole map ─────────────────────────
 *
 * DESIGN-SYSTEM.md §4 exempts the map canvas itself from screen-reader requirements
 * *on the condition* that its data is reachable through the OOB and contact lists.
 * That makes this panel load-bearing, not a convenience: it is the only way a
 * screen-reader user reaches the tactical picture.
 *
 * Consequently it is built from plain nested lists and buttons rather than a custom
 * `tree` widget. A half-implemented tree — roles without full arrow-key roving focus —
 * is worse for a screen reader than a well-labelled list, because it promises an
 * interaction model it does not honour. Native focus order works everywhere.
 */
import { useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, Search, X } from 'lucide-react'
import { useDeck } from '../state/store'
import { affiliationLabel, affiliationToken } from '../lib/sidc'
import { contactAge, formatLatLon, thousands } from '../lib/format'
import type { OrbisContact, OrbisEntity } from '../data/types'

type Tab = 'orbat' | 'contacts'

/** Units group by mission where they have one, else by domain. Mirrors how an
 *  operator thinks about their own force. */
function groupKey(e: OrbisEntity): string {
  if (e.mission) return e.mission
  return `Unassigned · ${e.domain}`
}

function damageState(e: OrbisEntity): 'ok' | 'degraded' | 'destroyed' {
  if (e.components.some((c) => c.health === 'destroyed')) return 'destroyed'
  if (e.components.some((c) => c.health === 'degraded')) return 'degraded'
  return 'ok'
}

export function OrbatPanel() {
  const open = useDeck((s) => s.orbatOpen)
  const toggle = useDeck((s) => s.toggleOrbat)
  const entities = useDeck((s) => s.entities)
  const contacts = useDeck((s) => s.contacts)
  const selection = useDeck((s) => s.selection)
  const select = useDeck((s) => s.select)
  const focusOn = useDeck((s) => s.focusOn)

  const [tab, setTab] = useState<Tab>('orbat')
  const [query, setQuery] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const q = query.trim().toLowerCase()

  const groups = useMemo(() => {
    const map = new Map<string, OrbisEntity[]>()
    for (const e of entities) {
      if (q && !`${e.designation} ${e.className} ${e.mission ?? ''}`.toLowerCase().includes(q)) continue
      const k = groupKey(e)
      map.set(k, [...(map.get(k) ?? []), e])
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [entities, q])

  const shownContacts = useMemo(
    () =>
      contacts
        .filter((c) => !q || `${c.track} ${c.classification}`.toLowerCase().includes(q))
        // Highest confidence first: the tracks an operator acts on come first.
        .sort((a, b) => b.confidence - a.confidence),
    [contacts, q],
  )

  if (!open) return null

  const pick = (kind: 'entity' | 'contact', id: string, lon: number, lat: number) => {
    select({ kind, id })
    focusOn({ lon, lat, zoom: 8 })
  }

  const toggleGroup = (k: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })

  return (
    <div className="panel absolute left-3 top-3 z-20 flex w-80 flex-col overflow-hidden" style={{ maxHeight: 'calc(100% - 24px)' }}>
      <div
        className="flex shrink-0 items-center justify-between px-3 py-2"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <span className="title">Order of battle</span>
        <button
          className="ctl"
          style={{ width: 20, height: 20, padding: 0 }}
          onClick={toggle}
          aria-label="Close order of battle panel"
        >
          <X size={12} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex shrink-0" style={{ borderBottom: '1px solid var(--color-line-2)' }} role="tablist">
        {(['orbat', 'contacts'] as Tab[]).map((t) => {
          const active = tab === t
          return (
            <button
              key={t}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t)}
              className="flex-1 py-1.5 transition-colors"
              style={{
                fontSize: 11,
                fontWeight: active ? 500 : 400,
                color: active ? 'var(--color-text-1)' : 'var(--color-text-3)',
                background: active ? 'var(--color-accent-dim)' : undefined,
                borderBottom: `2px solid ${active ? 'var(--color-accent)' : 'transparent'}`,
              }}
            >
              {t === 'orbat' ? `Own force (${entities.length})` : `Contacts (${contacts.length})`}
            </button>
          )
        })}
      </div>

      {/* Search */}
      <div className="flex shrink-0 items-center gap-1.5 px-3 py-1.5" style={{ borderBottom: '1px solid var(--color-line-1)' }}>
        <Search size={12} style={{ color: 'var(--color-text-3)' }} aria-hidden />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={tab === 'orbat' ? 'Search units…' : 'Search tracks…'}
          className="min-w-0 flex-1 bg-transparent outline-none"
          style={{ fontSize: 11.5, color: 'var(--color-text-1)' }}
          aria-label={tab === 'orbat' ? 'Search units' : 'Search tracks'}
        />
        {query && (
          <button
            className="ctl"
            style={{ width: 16, height: 16, padding: 0 }}
            onClick={() => setQuery('')}
            aria-label="Clear search"
          >
            <X size={10} />
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'orbat' && (
          <>
            {groups.length === 0 && (
              <p className="px-3 py-4 text-center" style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
                No units match “{query}”.
              </p>
            )}
            {groups.map(([name, units]) => {
              const isCollapsed = collapsed.has(name)
              return (
                <section key={name} aria-label={name}>
                  <button
                    className="flex w-full items-center gap-1.5 px-2 py-1 text-left transition-colors hover:bg-[var(--color-surface-2)]"
                    style={{ background: 'var(--color-surface-2)' }}
                    onClick={() => toggleGroup(name)}
                    aria-expanded={!isCollapsed}
                  >
                    {isCollapsed ? (
                      <ChevronRight size={11} style={{ color: 'var(--color-text-3)' }} aria-hidden />
                    ) : (
                      <ChevronDown size={11} style={{ color: 'var(--color-text-3)' }} aria-hidden />
                    )}
                    <span className="label min-w-0 flex-1 truncate" style={{ fontSize: 9 }}>
                      {name}
                    </span>
                    <span className="num" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
                      {units.length}
                    </span>
                  </button>

                  {!isCollapsed &&
                    units.map((e) => {
                      const dmg = damageState(e)
                      const isSel = selection?.id === e.id
                      return (
                        <button
                          key={e.id}
                          className="flex w-full items-center gap-2 px-3 py-1 text-left transition-colors hover:bg-[var(--color-surface-2)]"
                          style={{
                            borderBottom: '1px solid var(--color-line-1)',
                            background: isSel ? 'var(--color-accent-dim)' : undefined,
                          }}
                          onClick={() => pick('entity', e.id, e.lon, e.lat)}
                          aria-current={isSel ? 'true' : undefined}
                          aria-label={`${e.designation}, ${e.className}, ${affiliationLabel(e.affiliation as never)}, ${e.readiness}, fuel ${Math.round(e.fuelFraction * 100)} percent${dmg !== 'ok' ? `, ${dmg}` : ''}, at ${formatLatLon(e.lat, e.lon)}`}
                        >
                          <span
                            className="h-4 w-0.5 shrink-0 rounded-full"
                            style={{ background: affiliationToken(e.affiliation as never) }}
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1">
                            <span className="num block truncate" style={{ fontSize: 11 }}>
                              {e.designation}
                            </span>
                            <span className="block truncate" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
                              {e.className}
                            </span>
                          </span>
                          {dmg !== 'ok' && (
                            <AlertTriangle
                              size={10}
                              style={{
                                color: dmg === 'destroyed' ? 'var(--color-crit)' : 'var(--color-warn)',
                              }}
                              aria-hidden
                            />
                          )}
                          <span
                            className="num shrink-0"
                            style={{
                              fontSize: 10,
                              color:
                                e.fuelFraction > 0.4
                                  ? 'var(--color-text-3)'
                                  : e.fuelFraction > 0.2
                                    ? 'var(--color-warn)'
                                    : 'var(--color-crit)',
                            }}
                          >
                            {Math.round(e.fuelFraction * 100)}%
                          </span>
                        </button>
                      )
                    })}
                </section>
              )
            })}
          </>
        )}

        {tab === 'contacts' && (
          <>
            <p className="px-3 py-1.5" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
              Sensor-derived estimates held by BLUE — not ground truth.
            </p>
            {shownContacts.length === 0 && (
              <p className="px-3 py-4 text-center" style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
                No tracks match “{query}”.
              </p>
            )}
            {shownContacts.map((c: OrbisContact) => {
              const isSel = selection?.id === c.id
              const age = contactAge(c.ageSec)
              return (
                <button
                  key={c.id}
                  className="flex w-full items-center gap-2 px-3 py-1 text-left transition-colors hover:bg-[var(--color-surface-2)]"
                  style={{
                    borderBottom: '1px solid var(--color-line-1)',
                    background: isSel ? 'var(--color-accent-dim)' : undefined,
                    opacity: age.tier === 'stale' ? 0.6 : 1,
                  }}
                  onClick={() => pick('contact', c.id, c.lon, c.lat)}
                  aria-current={isSel ? 'true' : undefined}
                  aria-label={`${c.track}, ${c.classification}, ${affiliationLabel(c.affiliation as never)}, confidence ${c.confidence} percent, position uncertainty plus or minus ${c.uncertaintyNm} nautical miles, last updated ${age.text} ago, detected by ${c.detectedBy}`}
                >
                  <span
                    className="h-4 w-0.5 shrink-0 rounded-full"
                    style={{ background: affiliationToken(c.affiliation as never) }}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="num block truncate" style={{ fontSize: 11 }}>
                      {c.track}
                    </span>
                    <span className="block truncate" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
                      {c.classification}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span
                      className="num block"
                      style={{
                        fontSize: 10,
                        color: c.confidence >= 70 ? 'var(--color-text-2)' : 'var(--color-warn)',
                      }}
                    >
                      {c.confidence}%
                    </span>
                    <span className="num block" style={{ fontSize: 9, color: 'var(--color-text-3)' }}>
                      {age.text}
                    </span>
                  </span>
                </button>
              )
            })}
          </>
        )}
      </div>

      <div
        className="shrink-0 px-3 py-1"
        style={{ borderTop: '1px solid var(--color-line-2)', background: 'var(--color-surface-2)' }}
      >
        <span className="num" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
          {tab === 'orbat'
            ? `${groups.reduce((a, [, u]) => a + u.length, 0)} shown of ${thousands(entities.length)}`
            : `${shownContacts.length} shown of ${thousands(contacts.length)}`}
        </span>
      </div>
    </div>
  )
}
